import { type DateObject, type DateType, useIntl } from '@capsicum/common';
import { ListCard, Timer } from '@capsicum/common-web';
import { type FanTokenOfferingCardStatus } from '@capsicum/features/fan-token-offering/shared';
import { type ReactNode } from 'react';
import { match } from 'ts-pattern';

import * as S from './FanTokenOffering.styled';

const { RibbonText } = ListCard.Ribbon;

type FanTokenOfferingCardRibbonProps = {
	claimableExpirationDate: DateType;
	claimableStartDate: DateType;
	handleElapsed: () => void;
	parsedStatus: FanTokenOfferingCardStatus;
	shortCode: string;
	startDate: DateObject;
};

export const FanTokenOfferingCardRibbon = ({
	claimableExpirationDate,
	claimableStartDate,
	handleElapsed,
	parsedStatus,
	shortCode,
	startDate
}: FanTokenOfferingCardRibbonProps) => {
	const { $t } = useIntl();

	return match(parsedStatus)
		.with('aboutToStart', () => (
			<S.FtoStarting color="main.enabled" variant="caption1">
				{$t('fanTokenOffering.starting')}
			</S.FtoStarting>
		))
		.with('published', () => (
			<>
				<S.AvailableIn color="main.enabled" variant="caption2">
					{$t('rewards.fanshop.availableIn')}
				</S.AvailableIn>
				<Timer
					customFormat="digitalTimer"
					expirationDate={startDate}
					size="xsmall"
					testID={`ftoCard-${shortCode}-digitalTimer`}
					type="default"
					onElapsed={handleElapsed}
				/>
			</>
		))
		.with('opened', () => (
			<S.AvailableNow color="main.enabled" variant="caption1">
				{$t('fanTokenOffering.availableNow')}
			</S.AvailableNow>
		))
		.with('finishedWithoutPurchase', () => (
			<RibbonText isBold isUppperCase>
				{$t('fanTokenOffering.soldOut')}
			</RibbonText>
		))
		.with('delayedClaimPeriod', () => (
			<S.AvailableIn color="main.enabled" variant="caption2">
				{$t('fanTokenOffering.claim.claimPeriodDelayed', {
					strong: (chunks: Array<ReactNode>) => <S.Bold>{chunks}</S.Bold>
				})}
			</S.AvailableIn>
		))
		.with('waitingForClaimPeriodToStart', () => (
			<>
				<S.AvailableIn color="main.enabled" variant="caption2">
					{$t('fanTokenOffering.claim.claimIn', {
						strong: (chunks: Array<ReactNode>) => <S.Bold>{chunks}</S.Bold>
					})}
				</S.AvailableIn>
				<Timer
					customFormat="digitalTimer"
					expirationDate={claimableStartDate}
					size="xsmall"
					testID={`ftoCard-${shortCode}-digitalTimer`}
					type="default"
				/>
			</>
		))
		.with('claimable', () => (
			<>
				<S.AvailableIn color="main.enabled" variant="caption2">
					{$t('fanTokenOffering.claim.endedTitle', {
						strong: (chunks: Array<ReactNode>) => <S.Bold>{chunks}</S.Bold>
					})}
				</S.AvailableIn>
				<Timer
					customFormat="digitalTimer"
					expirationDate={claimableExpirationDate}
					size="xsmall"
					testID={`ftoCard-${shortCode}-digitalTimer`}
					type="default"
				/>
			</>
		))
		.with('endedClaimPeriod', () => (
			<RibbonText isBold isUppperCase>
				{$t('fanTokenOffering.claim.ended')}
			</RibbonText>
		))
		.with(null, () => null)
		.exhaustive();
};
